import { useState, useEffect, useCallback } from 'react';

const DEVICE_CACHE_KEY = 'deviceFingerprint';

// Lightweight device fingerprint (secure proxy for MAC)
const generateFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('device-fingerprint-v2', 2, 2); // Versioned for updates
  const hash = btoa(canvas.toDataURL()).substring(0, 32); // Truncated hash

  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    platform: navigator.platform,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    canvasHash: hash,
  };
};

// Collect core device info (synchronous, zero async)
const collectDeviceInfo = () => ({
  os: navigator.userAgent.includes('Windows') ? 'Windows' :
      navigator.userAgent.includes('Mac') ? 'macOS' :
      navigator.userAgent.includes('Linux') ? 'Linux' :
      navigator.userAgent.includes('Android') ? 'Android' :
      navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad') ? 'iOS' : 'Unknown',
  screenWidth: window.screen.width,
  screenHeight: window.screen.height,
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  ...generateFingerprint(),
  timestamp: Date.now(),
});

export const useDeviceValidator = () => {
    const [isValidating, setIsValidating] = useState(false);
    const [cachedInfo, setCachedInfo] = useState(() => {
        try {
        const cached = localStorage.getItem(DEVICE_CACHE_KEY);
        return cached ? JSON.parse(cached) : null;
        } catch {
        return null; // Fallback on storage errors
        }
    });

    // Collect and cache on mount (instant)
    useEffect(() => {
        const collectAndSend = () => {
            if (cachedInfo) return; // Reuse cache

            setIsValidating(true);
            const fullInfo = collectDeviceInfo();

            // Cache for session (or until manual clear)
            try {
                localStorage.setItem(DEVICE_CACHE_KEY, JSON.stringify(fullInfo));
            } catch {
                // Graceful ignore (storage quota full?)
            }
            setCachedInfo(fullInfo);
            setIsValidating(false);
        };

        collectAndSend();

        // Debounced resize (passive listener for perf)
        let timeout;
        const handleResize = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            if (cachedInfo) {
                const updated = {
                    ...cachedInfo,
                    screenWidth: window.screen.width,
                    screenHeight: window.screen.height,
                    viewportWidth: window.innerWidth,
                    viewportHeight: window.innerHeight,
                    timestamp: Date.now(),
                };
                try {
                    localStorage.setItem(DEVICE_CACHE_KEY, JSON.stringify(updated));
                    setCachedInfo(updated);
                } catch {}
            }
        }, 250);
        };
        window.addEventListener('resize', handleResize, { passive: true });

        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeout);
        };
    }, [cachedInfo]); // Depend on cachedInfo to avoid loops

    // Wrapper for actions (non-blocking)
    const validateAction = useCallback((actionFn) => (...args) => {
        if (isValidating || !cachedInfo) return actionFn(...args);
        return actionFn(...args);
    }, [cachedInfo, isValidating]);

    return { deviceInfo: cachedInfo, validateAction, isValidating };
};
